import React, { useState, useEffect } from 'react';
import { useMutation } from 'react-query';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    Box,
    Alert,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    InputAdornment
} from '@mui/material';
import { CatalogService } from '../../../../service/catalogService';
import { SelectLocation } from '../../../common';

const ImportDialog = ({ open, onClose, product, onSuccess }) => {
    const [formData, setFormData] = useState({
        supplierId: null,
        locationId: null,
        supplierCost: '',
        landingCost: '',
        retailPrice: '',
        markupMultiplier: '1.0',
        isTaxable: false, // Default to false
        defaultTaxPercentage: '',
        profit: '',
        profitPercentage: '',
        quantity: 1,
        notes: ''
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        try {
            const supplierCost = parseFloat(formData.supplierCost) || 0;
            const landingCost = parseFloat(formData.landingCost) || 0;
            const retailPrice = parseFloat(formData.retailPrice) || 0;
            const markupMultiplier = parseFloat(formData.markupMultiplier) || 1.0;

            if (supplierCost < 0 || landingCost < 0 || retailPrice < 0 || markupMultiplier < 0) {
                return;
            }

            const totalCost = supplierCost + landingCost;
            const sellingPrice = retailPrice * markupMultiplier;
            const profit = sellingPrice - totalCost;
            const profitPercentage = sellingPrice ? (profit / sellingPrice) * 100 : 0;
            
            setFormData(prev => ({
                ...prev,
                profit: profit.toFixed(2),
                profitPercentage: profitPercentage.toFixed(2)
            }));
        } catch (error) {
            console.error('Error calculating profit and profit percentage:', error);
        }
    }, [formData.supplierCost, formData.landingCost, formData.retailPrice, formData.markupMultiplier]);
    // Mutation hook for importing product to inventory
    // Uses react-query's useMutation to handle async state and side effects
    const importMutation = useMutation(
        // Mutation function that calls the API endpoint
        (data) => CatalogService.importToInventory(data),
        {
            // On successful import, call the success callback passed as prop
            onSuccess: () => {
                onSuccess();
            },
            // On error, set error message in form state
            // Uses optional chaining to safely access error response data
            onError: (error) => {
                setErrors({
                    submit: error.response?.data?.message || 'Failed to import product'
                });
            }
        }
    );

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const updateLocation = (location) => {
        setFormData(prev => ({ ...prev, locationId: location?.id || null }));
        // Clear location error
        if (errors.locationId) {
            setErrors(prev => ({ ...prev, locationId: null }));
        }
    };

    const validate = () => {
        const newErrors = {};
        
        if (!formData.locationId) {
            newErrors.locationId = 'Location is required';
        }
        if (!formData.supplierCost || parseFloat(formData.supplierCost) < 0) {
            newErrors.supplierCost = 'Valid supplier cost is required';
        }
        if (formData.landingCost && parseFloat(formData.landingCost) < 0) {
            newErrors.landingCost = 'Landing cost cannot be negative';
        }
        if (!formData.retailPrice || parseFloat(formData.retailPrice) < 0) {
            newErrors.retailPrice = 'Valid retail price is required';
        }
        if (!formData.markupMultiplier || parseFloat(formData.markupMultiplier) < 0) {
            newErrors.markupMultiplier = 'Valid markup multiplier is required';
        }
        if (!formData.quantity || parseInt(formData.quantity) < 0) {
            newErrors.quantity = 'Valid quantity is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;

        importMutation.mutate({
            catalogProductId: product.id,
            supplierId: formData.supplierId,
            locationId: formData.locationId,
            supplierCost: parseFloat(formData.supplierCost),
            landingCost: parseFloat(formData.landingCost) || 0,
            retailPrice: parseFloat(formData.retailPrice),
            markupMultiplier: parseFloat(formData.markupMultiplier),
            isTaxable: formData.isTaxable,
            defaultTaxPercentage: parseFloat(formData.defaultTaxPercentage) || 0,
            quantity: parseInt(formData.quantity),
            notes: formData.notes
        });
    };

    if (!product) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Initial Stock Add
            </DialogTitle>
            <DialogContent>
                {/* Product Summary */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Product
                    </Typography>
                    <Typography variant="h6">
                        {product.brand?.name} - {product.frame?.modelNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {product.frame?.shape?.name} | {product.frame?.material?.name}
                        {product.frame?.colorName && ` | ${product.frame.colorName}`}
                    </Typography>
                    {product.frame?.upcOrEan && (
                        <Typography variant="caption" color="text.secondary">
                            UPC: {product.frame.upcOrEan}
                        </Typography>
                    )}
                </Box>

                {errors.submit && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errors.submit}
                    </Alert>
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <SelectLocation 
                            fullWidth={true} 
                            location={formData.locationId || ''} 
                            error={!!errors.locationId}
                            required={true}
                            helperText={errors.locationId} 
                            setLocation={updateLocation}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.isTaxable}
                                    onChange={(e) => handleChange('isTaxable', e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Is Taxable"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Default Tax %"
                            type="text"
                            value={formData.defaultTaxPercentage}
                            onChange={(e) => handleChange('defaultTaxPercentage', e.target.value)}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Supplier Cost *"
                            type="text"
                            value={formData.supplierCost}
                            onChange={(e) => handleChange('supplierCost', e.target.value)}
                            error={!!errors.supplierCost}
                            helperText={errors.supplierCost}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Landing Cost"
                            type="text"
                            value={formData.landingCost}
                            onChange={(e) => handleChange('landingCost', e.target.value)}
                            error={!!errors.landingCost}
                            helperText={errors.landingCost}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Retail Price *"
                            type="text"
                            value={formData.retailPrice}
                            onChange={(e) => handleChange('retailPrice', e.target.value)}
                            error={!!errors.retailPrice}
                            helperText={errors.retailPrice}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Markup Multiplier *"
                            type="text"
                            value={formData.markupMultiplier}
                            onChange={(e) => handleChange('markupMultiplier', e.target.value)}
                            error={!!errors.markupMultiplier}
                            helperText={errors.markupMultiplier}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Profit"
                            type="text"
                            value={formData.profit}
                            disabled
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Profit Percentage"
                            type="text"
                            value={formData.profitPercentage}
                            disabled
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Quantity *"
                            type="text"
                            value={formData.quantity}
                            onChange={(e) => handleChange('quantity', e.target.value)}
                            error={!!errors.quantity}
                            helperText={errors.quantity}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Notes"
                            multiline
                            rows={3}
                            value={formData.notes}
                            onChange={(e) => handleChange('notes', e.target.value)}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={importMutation.isLoading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={importMutation.isLoading}
                    startIcon={importMutation.isLoading && <CircularProgress size={20} />}
                >
                    {importMutation.isLoading ? 'Importing...' : 'Import'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImportDialog;
